import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type { ZuubManualCheckStatusResponse } from '@/@types/zuub/contracts';

import { makeAPIRequestReactQueryWithHeaders } from '../api';
import { endpoints } from '../endpoints';

const POLL_INTERVAL_MS = 3000;

/**
 * Polls the manual-check status endpoint for a given requestId.
 * Only runs when requestId is non-null. Uses Practice and Org headers.
 */
export const useZuubManualCheckStatus = (requestId: string | null) => {
  const {
    primaryPracticeId: practiceId,
    primaryOrganizationId: organizationId,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  return useQuery<ZuubManualCheckStatusResponse>(
    ['zuubManualCheckStatus', requestId],
    () =>
      makeAPIRequestReactQueryWithHeaders<ZuubManualCheckStatusResponse>(
        endpoints.zuubManualCheckStatus(requestId!),
        {},
        {
          Org: organizationId,
          Practice: practiceId,
        },
        'GET',
      ),
    {
      enabled: Boolean(requestId && practiceId && organizationId),
      refetchInterval: (data) => {
        const status = data?.asyncStatus;
        if (status === 'completed' || status === 'failed' || status === 'not_supported') {
          return false;
        }
        return POLL_INTERVAL_MS;
      },
      staleTime: 0,
    },
  );
};
